import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sun, Lightning, ShieldCheck, CurrencyInr, ChartLineUp, Users, CheckCircle,
  ArrowRight, Phone, HouseLine, Buildings, Factory, Wrench, Quotes, Star,
  MapPin, Calendar, Leaf
} from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import api, { IMAGES, CONTACT } from "../lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data.slice(0, 3))).catch(() => {});
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO page="home" titleFallback="Orax Solar — Best Solar Company in Akola" />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-24 hero-accent overflow-hidden">
        <div className="container-custom grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#135934]/10 text-[#135934] text-xs font-bold mb-5">
                <span className="w-2 h-2 rounded-full bg-[#FDB813] pulse-dot"></span>
                AKOLA'S #1 SOLAR COMPANY · SINCE 2015
              </div>
              <h1 className="h1 text-[#0A1F15] mb-5">
                Cut your electricity bill by <span className="text-[#135934]">up to 90%</span> with Solar
              </h1>
              <p className="text-lg md:text-xl text-[#2D4739] max-w-xl mb-7 leading-relaxed">
                {content?.hero_subheadline || "Akola's trusted solar installation company. Get government subsidy up to ₹78,000, 25-year warranty, and ROI within 4 years."}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <a href="#lead-form" className="btn-primary text-base" data-testid="hero-cta-quote">
                  <Sun weight="fill" size={18}/> Get Free Solar Quote
                </a>
                <a href={`tel:${CONTACT.phonePrimary}`} className="btn-outline-dark text-base" data-testid="hero-cta-call">
                  <Phone weight="bold" size={18}/> Call {CONTACT.phonePrimary}
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-6 text-sm text-[#2D4739]">
                <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[#135934]" size={18}/> MNRE Empanelled</div>
                <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[#135934]" size={18}/> 25-year warranty</div>
                <div className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[#135934]" size={18}/> ₹78,000 subsidy</div>
              </div>
            </motion.div>
          </div>

          <motion.div
            id="lead-form"
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={IMAGES.heroBackground}
                alt="Solar panels on rooftop"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="relative bg-white m-4 rounded-2xl p-6 md:p-7 shadow-xl">
                <div className="overline mb-1">GET FREE QUOTE</div>
                <h3 className="h3 mb-1 text-[#0A1F15]">Solar expert will call you in 30 min</h3>
                <p className="text-sm text-[#5C7368] mb-5">No spam. Just a straightforward solar consultation.</p>
                <LeadForm source="home-hero" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      {(content?.show_stats !== false) && (
        <section className="border-y border-[#E5E3DB] bg-white">
          <div className="container-custom grid grid-cols-2 md:grid-cols-4 gap-6 py-10">
            {[
              { v: content?.stat_projects || "500+", l: "Projects Completed", icon: <Buildings weight="duotone" size={28}/> },
              { v: content?.stat_savings || "₹12Cr+", l: "Customer Savings", icon: <CurrencyInr weight="duotone" size={28}/> },
              { v: content?.stat_satisfaction || "98%", l: "Customer Satisfaction", icon: <Star weight="duotone" size={28}/> },
              { v: content?.stat_experience || "10+", l: "Years of Experience", icon: <Calendar weight="duotone" size={28}/> },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{...fadeUp.transition, delay: i*0.05}} className="text-center">
                <div className="inline-flex w-12 h-12 rounded-xl bg-[#F0EFEB] items-center justify-center text-[#135934] mb-3">
                  {s.icon}
                </div>
                <div className="font-display font-extrabold text-3xl md:text-4xl text-[#0A1F15]">{s.v}</div>
                <div className="text-sm text-[#5C7368] mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* WHY CHOOSE US */}
      {(content?.show_why_us !== false) && (
      <section className="section-padding bg-[#F9F8F6]">
        <div className="container-custom">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <div className="overline mb-3">WHY CHOOSE ORAX SOLAR</div>
            <h2 className="h2 text-[#0A1F15]">The smart choice for <span className="text-[#135934]">solar in Maharashtra</span></h2>
          </motion.div>

          <div className="grid md:grid-cols-12 gap-6">
            {[
              { icon: <ShieldCheck weight="duotone" size={32}/>, title: "MNRE Empanelled", desc: "Government-approved vendor for PM Surya Ghar Yojana. Subsidies handled end-to-end.", span: "md:col-span-5" },
              { icon: <Lightning weight="duotone" size={32}/>, title: "Tier-1 Panels Only", desc: "Only genuine Waaree, Adani, Tata Power panels. 25-year output warranty.", span: "md:col-span-4" },
              { icon: <ChartLineUp weight="duotone" size={32}/>, title: "Fast ROI", desc: "Break even in less than 4 years. 25+ years of free electricity after that.", span: "md:col-span-3" },
              { icon: <CurrencyInr weight="duotone" size={32}/>, title: "Easy EMI Options", desc: "0% down payment schemes with partner banks. EMI less than your current bill.", span: "md:col-span-3" },
              { icon: <Wrench weight="duotone" size={32}/>, title: "10-year Inverter Warranty", desc: "Premium inverters from Luminous, Growatt, Microtek with extended warranty.", span: "md:col-span-5" },
              { icon: <MapPin weight="duotone" size={32}/>, title: "Local Akola Team", desc: "Same-day service across Akola, Amravati, Washim, Buldhana and Yavatmal.", span: "md:col-span-4" },
            ].map((f, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{...fadeUp.transition, delay: i*0.05}}
                className={`${f.span} bg-white rounded-2xl p-7 border border-[#E5E3DB] card-lift`}
                data-testid={`why-us-card-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#135934]/10 text-[#135934] flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="h3 mb-2 text-[#0A1F15]">{f.title}</h3>
                <p className="text-[#2D4739]">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SERVICES BENTO */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="overline mb-3">SOLAR SOLUTIONS</div>
              <h2 className="h2 text-[#0A1F15]">Powering every kind of building</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-[#0F52BA] font-semibold hover:gap-3 transition-all" data-testid="services-see-all">
              See all services <ArrowRight weight="bold" size={18}/>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-12 gap-5">
            <Link to="/services/residential" className="md:col-span-7 group relative rounded-3xl overflow-hidden h-80 md:h-[420px] card-lift" data-testid="service-residential">
              <img src={IMAGES.residential} alt="Residential solar" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F15] via-[#0A1F15]/30 to-transparent"/>
              <div className="relative h-full flex flex-col justify-end p-7 text-white">
                <div className="flex items-center gap-2 mb-2"><HouseLine weight="fill" size={20} className="text-[#FDB813]"/><span className="text-xs font-bold uppercase tracking-widest text-[#FDB813]">Residential</span></div>
                <h3 className="font-display font-extrabold text-3xl md:text-4xl mb-2">Home Rooftop Solar</h3>
                <p className="text-white/85 mb-3 max-w-md">1kW to 10kW systems with ₹78,000 subsidy. Payback in 3-4 years.</p>
                <span className="inline-flex items-center gap-1.5 text-[#FDB813] text-sm font-semibold">Learn more <ArrowRight weight="bold" size={16}/></span>
              </div>
            </Link>

            <Link to="/services/commercial" className="md:col-span-5 group relative rounded-3xl overflow-hidden h-80 md:h-[420px] card-lift" data-testid="service-commercial">
              <img src={IMAGES.commercial} alt="Commercial solar" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F52BA] via-[#0F52BA]/40 to-transparent"/>
              <div className="relative h-full flex flex-col justify-end p-7 text-white">
                <div className="flex items-center gap-2 mb-2"><Buildings weight="fill" size={20} className="text-[#FDB813]"/><span className="text-xs font-bold uppercase tracking-widest text-[#FDB813]">Commercial</span></div>
                <h3 className="font-display font-extrabold text-3xl mb-2">Business Solar</h3>
                <p className="text-white/85 mb-3">Offices, shops, hotels. Reduce OPEX by 70%.</p>
                <span className="inline-flex items-center gap-1.5 text-[#FDB813] text-sm font-semibold">Learn more <ArrowRight weight="bold" size={16}/></span>
              </div>
            </Link>

            <Link to="/services/industrial" className="md:col-span-5 group relative rounded-3xl overflow-hidden h-72 card-lift" data-testid="service-industrial">
              <img src={IMAGES.texture} alt="Industrial solar" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F15] to-transparent"/>
              <div className="relative h-full flex flex-col justify-end p-7 text-white">
                <div className="flex items-center gap-2 mb-2"><Factory weight="fill" size={20} className="text-[#FDB813]"/><span className="text-xs font-bold uppercase tracking-widest text-[#FDB813]">Industrial</span></div>
                <h3 className="font-display font-extrabold text-3xl mb-2">Industrial Solar</h3>
                <p className="text-white/85 mb-3">MW-scale plants, captive PPA, OPEX models.</p>
                <span className="inline-flex items-center gap-1.5 text-[#FDB813] text-sm font-semibold">Learn more <ArrowRight weight="bold" size={16}/></span>
              </div>
            </Link>

            <Link to="/services/maintenance" className="md:col-span-7 group relative rounded-3xl overflow-hidden h-72 card-lift" data-testid="service-maintenance">
              <img src={IMAGES.worker} alt="Solar maintenance" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F15] via-[#0A1F15]/50 to-transparent"/>
              <div className="relative h-full flex items-center p-8 text-white">
                <div className="max-w-md">
                  <div className="flex items-center gap-2 mb-2"><Wrench weight="fill" size={20} className="text-[#FDB813]"/><span className="text-xs font-bold uppercase tracking-widest text-[#FDB813]">AMC & Service</span></div>
                  <h3 className="font-display font-extrabold text-3xl mb-2">Solar Maintenance</h3>
                  <p className="text-white/85 mb-3">Professional cleaning, inspection & AMC starting ₹3,500/year.</p>
                  <span className="inline-flex items-center gap-1.5 text-[#FDB813] text-sm font-semibold">Learn more <ArrowRight weight="bold" size={16}/></span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      {(content?.show_process !== false) && (
      <section className="section-padding bg-[#F0EFEB]">
        <div className="container-custom">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <div className="overline mb-3">HOW IT WORKS</div>
            <h2 className="h2 text-[#0A1F15]">From survey to savings in 15 days</h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", title: "Free Site Survey", desc: "Our engineer visits your location, measures roof, analyzes shadow & your 12-month bill." },
              { n: "02", title: "Custom Proposal", desc: "Detailed quote with panel brand, inverter, subsidy calculation and payback analysis." },
              { n: "03", title: "Installation", desc: "Turnkey installation in 3-5 days by certified technicians. DISCOM approvals handled." },
              { n: "04", title: "You Save", desc: "Net meter installed, system goes live. You start saving from day one, for 25+ years." },
            ].map((step, i) => (
              <motion.div key={i} {...fadeUp} transition={{...fadeUp.transition, delay: i*0.08}} className="relative">
                <div className="font-display font-black text-7xl text-[#135934]/15 leading-none mb-3">{step.n}</div>
                <h3 className="h3 mb-2 text-[#0A1F15]">{step.title}</h3>
                <p className="text-[#2D4739]">{step.desc}</p>
                {i < 3 && <div className="hidden md:block absolute top-6 -right-3 w-6 h-0.5 bg-[#135934]/30"/>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* PROJECTS PREVIEW */}
      {(content?.show_projects !== false) && projects.length > 0 && (
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="overline mb-3">RECENT PROJECTS</div>
              <h2 className="h2 text-[#0A1F15]">Real installations, real savings</h2>
            </div>
            <Link to="/projects" className="inline-flex items-center gap-2 text-[#0F52BA] font-semibold hover:gap-3 transition-all">
              View all projects <ArrowRight weight="bold" size={18}/>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <motion.div key={p.id} {...fadeUp} transition={{...fadeUp.transition, delay: i*0.08}} className="bg-white rounded-2xl overflow-hidden border border-[#E5E3DB] card-lift">
                <div className="aspect-[4/3] overflow-hidden bg-[#F0EFEB]">
                  {p.after_image && <img src={p.after_image} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>}
                </div>
                <div className="p-6">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">{p.category}</div>
                  <h3 className="font-display font-bold text-xl mb-2 text-[#0A1F15]">{p.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-[#5C7368] mb-3">
                    <span className="flex items-center gap-1"><MapPin size={14}/>{p.location}</span>
                    <span className="flex items-center gap-1"><Lightning size={14}/>{p.system_size}</span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#135934]/10 text-[#135934] text-sm font-bold">
                    <Leaf weight="fill" size={14}/> Saves {p.savings}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      {(content?.show_testimonials !== false) && testimonials.length > 0 && (
      <section className="section-padding bg-[#0A1F15] text-white">
        <div className="container-custom">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <div className="overline mb-3 text-[#FDB813]">TESTIMONIALS</div>
            <h2 className="h2 text-white">Trusted by 500+ families & businesses</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t, i) => (
              <motion.div key={t.id} {...fadeUp} transition={{...fadeUp.transition, delay: i*0.08}} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                <Quotes weight="fill" size={28} className="text-[#FDB813] mb-4"/>
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating || 5)].map((_, j) => <Star key={j} weight="fill" size={16} className="text-[#FDB813]"/>)}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed">"{t.message}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="w-10 h-10 rounded-full bg-[#FDB813] text-[#0A1F15] flex items-center justify-center font-bold">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-white/60">{t.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FINAL CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="relative rounded-3xl overflow-hidden bg-[#135934] p-10 md:p-16 text-white">
            <div className="absolute inset-0 opacity-20">
              <img src={IMAGES.texture} alt="" className="w-full h-full object-cover"/>
            </div>
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FDB813] text-[#0A1F15] text-xs font-bold mb-5">
                  <Lightning weight="fill" size={14}/> LIMITED SUBSIDY SLOTS
                </div>
                <h2 className="font-display font-extrabold text-4xl md:text-5xl leading-tight mb-4">
                  Start saving on your bill this month
                </h2>
                <p className="text-white/85 text-lg mb-7">
                  Join 500+ happy customers in Akola. Get a free consultation and custom solar quote today.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="#lead-form" onClick={() => window.scrollTo({top:0,behavior:"smooth"})} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FDB813] text-[#0A1F15] font-bold hover:-translate-y-0.5 transition" data-testid="final-cta-quote">
                    <Sun weight="fill" size={18}/> Get Free Quote
                  </a>
                  <a href={`tel:${CONTACT.phonePrimary}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/30 text-white font-semibold hover:bg-white/20 transition" data-testid="final-cta-call">
                    <Phone weight="bold" size={18}/> Call {CONTACT.phonePrimary}
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                  <div className="font-display font-extrabold text-3xl text-[#FDB813] mb-1">30 min</div>
                  <div className="text-sm text-white/80">Expert call back</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                  <div className="font-display font-extrabold text-3xl text-[#FDB813] mb-1">Free</div>
                  <div className="text-sm text-white/80">Site survey</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                  <div className="font-display font-extrabold text-3xl text-[#FDB813] mb-1">₹78K</div>
                  <div className="text-sm text-white/80">Subsidy available</div>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6">
                  <div className="font-display font-extrabold text-3xl text-[#FDB813] mb-1">25 yrs</div>
                  <div className="text-sm text-white/80">Panel warranty</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
